import React, { useState, useRef, useEffect } from 'react';
import './RectangleDrawer.css';

interface Line {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

interface FilledArea {
  id: string;
  points: {x: number, y: number}[];
}

const RectangleDrawer: React.FC = () => {
  const [lines, setLines] = useState<Line[]>([]);
  const [history, setHistory] = useState<Line[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isErasing, setIsErasing] = useState(false);
  const [isMeasuring, setIsMeasuring] = useState(false);
  const [showMeasurements, setShowMeasurements] = useState(false);
  const [showFillTool, setShowFillTool] = useState(false);
  const [measurementLine, setMeasurementLine] = useState<Line | null>(null);
  const [measuredSegments, setMeasuredSegments] = useState<Line[]>([]);
  const [persistentMeasuredSegments, setPersistentMeasuredSegments] = useState<Set<string>>(new Set());
  const [filledAreas, setFilledAreas] = useState<FilledArea[]>([]);
  const [startPoint, setStartPoint] = useState<{x: number, y: number} | null>(null);
  const [currentRect, setCurrentRect] = useState<{x1: number, y1: number, x2: number, y2: number} | null>(null);
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  
  const GRID_SIZE = 20;
  const CANVAS_WIDTH = 800;
  const CANVAS_HEIGHT = 600;

  // Snap coordinate to grid
  const snapToGrid = (coord: number) => {
    return Math.round(coord / GRID_SIZE) * GRID_SIZE;
  };

  // Get mouse coordinates relative to SVG
  const getMouseCoords = (event: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    
    const rect = svg.getBoundingClientRect();
    const x = snapToGrid(event.clientX - rect.left);
    const y = snapToGrid(event.clientY - rect.top);
    return { x, y };
  };

  // Check if a point is on a line segment
  const isPointOnLine = (px: number, py: number, line: Line, tolerance: number = 5) => {
    const { x1, y1, x2, y2 } = line;
    
    // Check if point is within bounding box of line
    if (px < Math.min(x1, x2) - tolerance || px > Math.max(x1, x2) + tolerance ||
        py < Math.min(y1, y2) - tolerance || py > Math.max(y1, y2) + tolerance) {
      return false;
    }
    
    // For horizontal lines
    if (Math.abs(y1 - y2) < tolerance) {
      return Math.abs(py - y1) < tolerance;
    }
    
    // For vertical lines
    if (Math.abs(x1 - x2) < tolerance) {
      return Math.abs(px - x1) < tolerance;
    }
    
    return false;
  };

  // Create rectangle lines from two corner points
  const createRectangleLines = (x1: number, y1: number, x2: number, y2: number): Line[] => {
    const minX = Math.min(x1, x2);
    const maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);
    
    if (minX === maxX || minY === maxY) return []; // No rectangle if no area
    
    const timestamp = Date.now();
    return [
      { id: `${timestamp}-top`, x1: minX, y1: minY, x2: maxX, y2: minY },      // Top
      { id: `${timestamp}-right`, x1: maxX, y1: minY, x2: maxX, y2: maxY },   // Right
      { id: `${timestamp}-bottom`, x1: maxX, y1: maxY, x2: minX, y2: maxY },  // Bottom
      { id: `${timestamp}-left`, x1: minX, y1: maxY, x2: minX, y2: minY },    // Left
    ];
  };

  // Calculate line length in grid units
  const getLineLength = (line: Line) => {
    const dx = Math.abs(line.x2 - line.x1);
    const dy = Math.abs(line.y2 - line.y1);
    return Math.max(dx, dy) / GRID_SIZE;
  };

  // Check if a point is inside any filled area
  const isPointInFilledArea = (x: number, y: number): boolean => {
    // Convert to grid coordinates
    const gridX = Math.floor(x / GRID_SIZE) * GRID_SIZE;
    const gridY = Math.floor(y / GRID_SIZE) * GRID_SIZE;
    
    for (const area of filledAreas) {
      if (area.points.some(p => p.x === gridX && p.y === gridY)) {
        return true;
      }
    }
    return false;
  };

  // Add to history for undo functionality
  const addToHistory = (newLines: Line[]) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push([...newLines]);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setLines(newLines);
    
    // Remove measurements for lines that no longer exist
    const lineIds = new Set(newLines.map(line => line.id));
    const updatedMeasurements = new Set<string>();
    persistentMeasuredSegments.forEach(id => {
      if (lineIds.has(id)) {
        updatedMeasurements.add(id);
      }
    });
    setPersistentMeasuredSegments(updatedMeasurements);
  };

  // Undo functionality
  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setLines([...history[newIndex]]);
    }
  };

  // Get current rectangle dimensions
  const getCurrentRectDimensions = () => {
    if (!currentRect) return null;
    const width = Math.abs(currentRect.x2 - currentRect.x1) / GRID_SIZE;
    const height = Math.abs(currentRect.y2 - currentRect.y1) / GRID_SIZE;
    return { width, height };
  };

  // Check if two line segments intersect
  const lineSegmentsIntersect = (line1: Line, line2: Line) => {
    const { x1, y1, x2, y2 } = line1;
    const { x1: x3, y1: y3, x2: x4, y2: y4 } = line2;
    
    // Check if lines are parallel (both horizontal or both vertical)
    const line1Horizontal = Math.abs(y1 - y2) < 5;
    const line1Vertical = Math.abs(x1 - x2) < 5;
    const line2Horizontal = Math.abs(y3 - y4) < 5;
    const line2Vertical = Math.abs(x3 - x4) < 5;
    
    // General line intersection for any two lines (not just horizontal/vertical)
    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    
    if (Math.abs(denom) < 0.01) {
      // Lines are parallel or coincident
      return false;
    }
    
    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;
    
    // Check if intersection point is within both line segments
    return (t >= 0 && t <= 1 && u >= 0 && u <= 1);
  };

  // Find intersection point of two lines
  const findIntersection = (line1: Line, line2: Line) => {
    const { x1, y1, x2, y2 } = line1;
    const { x1: x3, y1: y3, x2: x4, y2: y4 } = line2;
    
    const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
    
    if (Math.abs(denom) < 0.01) {
      return null; // Lines are parallel
    }
    
    const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
    
    // Calculate intersection point
    const intersectionX = x1 + t * (x2 - x1);
    const intersectionY = y1 + t * (y2 - y1);
    
    return { x: intersectionX, y: intersectionY };
  };

  // Check if a line segment overlaps with another line segment
  const segmentsOverlap = (line1: Line, line2: Line) => {
    // Both lines must be parallel (both horizontal or both vertical)
    const line1Horizontal = Math.abs(line1.y1 - line1.y2) < 1;
    const line1Vertical = Math.abs(line1.x1 - line1.x2) < 1;
    const line2Horizontal = Math.abs(line2.y1 - line2.y2) < 1;
    const line2Vertical = Math.abs(line2.x1 - line2.x2) < 1;
    
    if (line1Horizontal && line2Horizontal) {
      // Both horizontal - check if on same Y and X ranges overlap
      if (Math.abs(line1.y1 - line2.y1) < 1) {
        const line1MinX = Math.min(line1.x1, line1.x2);
        const line1MaxX = Math.max(line1.x1, line1.x2);
        const line2MinX = Math.min(line2.x1, line2.x2);
        const line2MaxX = Math.max(line2.x1, line2.x2);
        return !(line1MaxX <= line2MinX || line2MaxX <= line1MinX);
      }
    }
    
    if (line1Vertical && line2Vertical) {
      // Both vertical - check if on same X and Y ranges overlap
      if (Math.abs(line1.x1 - line2.x1) < 1) {
        const line1MinY = Math.min(line1.y1, line1.y2);
        const line1MaxY = Math.max(line1.y1, line1.y2);
        const line2MinY = Math.min(line2.y1, line2.y2);
        const line2MaxY = Math.max(line2.y1, line2.y2);
        return !(line1MaxY <= line2MinY || line2MaxY <= line1MinY);
      }
    }
    
    return false;
  };

  // Split all lines into segments at intersection points
  const splitAllLinesIntoSegments = (allLines: Line[]): Line[] => {
    const segments: Line[] = [];
    let segmentId = 0;
    
    // First, find all intersection points
    const intersectionPoints = new Map<string, Set<string>>(); // line id -> set of intersection point strings
    
    for (let i = 0; i < allLines.length; i++) {
      for (let j = i + 1; j < allLines.length; j++) {
        const line1 = allLines[i];
        const line2 = allLines[j];
        
        if (lineSegmentsIntersect(line1, line2)) {
          const intersection = findIntersection(line1, line2);
          if (intersection) {
            const pointStr = `${Math.round(intersection.x)},${Math.round(intersection.y)}`;
            
            if (!intersectionPoints.has(line1.id)) {
              intersectionPoints.set(line1.id, new Set());
            }
            if (!intersectionPoints.has(line2.id)) {
              intersectionPoints.set(line2.id, new Set());
            }
            
            intersectionPoints.get(line1.id)!.add(pointStr);
            intersectionPoints.get(line2.id)!.add(pointStr);
          }
        }
      }
    }
    
    // Now split each line at its intersection points
    allLines.forEach(line => {
      const lineIntersections = intersectionPoints.get(line.id);
      
      if (!lineIntersections || lineIntersections.size === 0) {
        // No intersections - keep the line as is
        segments.push({
          ...line,
          id: `seg-${segmentId++}`
        });
      } else {
        // Split the line at intersection points
        const isHorizontal = Math.abs(line.y1 - line.y2) < 5;
        const isVertical = Math.abs(line.x1 - line.x2) < 5;
        
        if (isHorizontal) {
          // Get all X coordinates (start, end, and intersections)
          const xCoords = [line.x1, line.x2];
          lineIntersections.forEach(pointStr => {
            const [x] = pointStr.split(',').map(Number);
            xCoords.push(x);
          });
          
          // Sort and remove duplicates
          const uniqueX = Array.from(new Set(xCoords)).sort((a, b) => a - b);
          
          // Create segments between consecutive X coordinates
          for (let i = 0; i < uniqueX.length - 1; i++) {
            segments.push({
              id: `seg-${segmentId++}`,
              x1: uniqueX[i],
              y1: line.y1,
              x2: uniqueX[i + 1],
              y2: line.y1
            });
          }
        } else if (isVertical) {
          // Get all Y coordinates (start, end, and intersections)
          const yCoords = [line.y1, line.y2];
          lineIntersections.forEach(pointStr => {
            const [, y] = pointStr.split(',').map(Number);
            yCoords.push(y);
          });
          
          // Sort and remove duplicates
          const uniqueY = Array.from(new Set(yCoords)).sort((a, b) => a - b);
          
          // Create segments between consecutive Y coordinates
          for (let i = 0; i < uniqueY.length - 1; i++) {
            segments.push({
              id: `seg-${segmentId++}`,
              x1: line.x1,
              y1: uniqueY[i],
              x2: line.x1,
              y2: uniqueY[i + 1]
            });
          }
        }
      }
    });
    
    return segments;
  };
  

  // Find the enclosed area starting from a point using flood fill
  const findEnclosedArea = (startX: number, startY: number): {x: number, y: number}[] | null => {
    // Create a grid representation of the canvas
    const gridWidth = Math.ceil(CANVAS_WIDTH / GRID_SIZE) + 1;
    const gridHeight = Math.ceil(CANVAS_HEIGHT / GRID_SIZE) + 1;
    const visited = new Array(gridHeight).fill(null).map(() => new Array(gridWidth).fill(false));
    
    // Convert pixel coordinates to grid coordinates
    const toGridX = (x: number) => Math.floor(x / GRID_SIZE);
    const toGridY = (y: number) => Math.floor(y / GRID_SIZE);
    
    // Check if there's a line between two grid cells
    const hasLineBetween = (x1: number, y1: number, x2: number, y2: number): boolean => {
      // Convert grid coordinates back to pixel coordinates (center of grid cell)
      const px1 = x1 * GRID_SIZE + GRID_SIZE / 2;
      const py1 = y1 * GRID_SIZE + GRID_SIZE / 2;
      const px2 = x2 * GRID_SIZE + GRID_SIZE / 2;
      const py2 = y2 * GRID_SIZE + GRID_SIZE / 2;
      
      // Check if any line segment blocks the path between these two points
      for (const line of lines) {
        // Check if the line intersects with the path between grid cells
        if (x1 === x2) {
          // Moving vertically
          const x = px1;
          const minY = Math.min(py1, py2);
          const maxY = Math.max(py1, py2);
          
          // Check if line is horizontal and crosses this vertical path
          if (Math.abs(line.y1 - line.y2) < 1 && 
              line.y1 > minY && line.y1 < maxY &&
              Math.min(line.x1, line.x2) <= x && Math.max(line.x1, line.x2) >= x) {
            return true;
          }
        } else if (y1 === y2) {
          // Moving horizontally
          const y = py1;
          const minX = Math.min(px1, px2);
          const maxX = Math.max(px1, px2);
          
          // Check if line is vertical and crosses this horizontal path
          if (Math.abs(line.x1 - line.x2) < 1 && 
              line.x1 > minX && line.x1 < maxX &&
              Math.min(line.y1, line.y2) <= y && Math.max(line.y1, line.y2) >= y) {
            return true;
          }
        }
      }
      return false;
    };
    
    // Start flood fill from the clicked point
    const startGridX = toGridX(startX);
    const startGridY = toGridY(startY);
    
    if (startGridX < 0 || startGridX >= gridWidth || startGridY < 0 || startGridY >= gridHeight) {
      return null;
    }
    
    const areaPoints: {x: number, y: number}[] = [];
    const queue: {x: number, y: number}[] = [{x: startGridX, y: startGridY}];
    visited[startGridY][startGridX] = true;
    
    // Flood fill algorithm
    while (queue.length > 0) {
      const {x, y} = queue.shift()!;
      areaPoints.push({x: x * GRID_SIZE, y: y * GRID_SIZE});
      
      // Check all 4 directions
      const directions = [[0, 1], [1, 0], [0, -1], [-1, 0]];
      for (const [dx, dy] of directions) {
        const nx = x + dx;
        const ny = y + dy;
        
        if (nx >= 0 && nx < gridWidth && ny >= 0 && ny < gridHeight &&
            !visited[ny][nx] && !hasLineBetween(x, y, nx, ny)) {
          visited[ny][nx] = true;
          queue.push({x: nx, y: ny});
        }
      }
    }
    
    return areaPoints.length > 0 ? areaPoints : null;
  };

  // Handle mouse down
  const handleMouseDown = (event: React.MouseEvent<SVGSVGElement>) => {
    const { x, y } = getMouseCoords(event);
    const ctrlPressed = event.ctrlKey || event.metaKey;
    
    if (showFillTool) {
      // Fill mode
      const area = findEnclosedArea(x, y);
      if (area) {
        const newFilledArea: FilledArea = {
          id: `fill-${Date.now()}`,
          points: area
        };
        setFilledAreas([...filledAreas, newFilledArea]);
      }
    } else if (showMeasurements) {
      // Measurement mode
      setIsMeasuring(true);
      setStartPoint({ x, y });
      setCurrentRect({ x1: x, y1: y, x2: x, y2: y });
      setMeasuredSegments([]);
    } else if (ctrlPressed) {
      // Rectangle eraser mode
      setIsErasing(true);
      setStartPoint({ x, y });
      setCurrentRect({ x1: x, y1: y, x2: x, y2: y });
    } else {
      // Drawing mode
      setIsDrawing(true);
      setStartPoint({ x, y });
      setCurrentRect({ x1: x, y1: y, x2: x, y2: y });
    }
  };

  // Handle mouse move
  const handleMouseMove = (event: React.MouseEvent<SVGSVGElement>) => {
    if ((!isDrawing && !isErasing && !isMeasuring) || !startPoint) return;
    
    const { x, y } = getMouseCoords(event);
    setCurrentRect({ x1: startPoint.x, y1: startPoint.y, x2: x, y2: y });
    
    // In measurement mode, find segments that intersect with current line
    if (isMeasuring) {
      const measureLine: Line = {
        id: 'measure',
        x1: startPoint.x,
        y1: startPoint.y,
        x2: x,
        y2: y
      };
      
      const intersectingSegments = lines.filter(segment => 
        lineSegmentsIntersect(segment, measureLine)
      );
      setMeasuredSegments(intersectingSegments);
    }
  };

  // Handle mouse up
  const handleMouseUp = (event: React.MouseEvent<SVGSVGElement>) => {
    const { x, y } = getMouseCoords(event);
    
    if (isMeasuring && startPoint) {
      // Measurement mode - save the measured segments persistently
      const newPersistentSegments = new Set(persistentMeasuredSegments);
      measuredSegments.forEach(segment => {
        newPersistentSegments.add(segment.id);
      });
      setPersistentMeasuredSegments(newPersistentSegments);
      
      setIsMeasuring(false);
      setStartPoint(null);
      setCurrentRect(null);
      setMeasuredSegments([]);
      return;
    }
    
    if (isErasing && startPoint) {
      // Line eraser mode
      const eraseLine: Line = {
        id: 'erase-line',
        x1: startPoint.x,
        y1: startPoint.y,
        x2: x,
        y2: y
      };
      
      // Only process if the erase line has some length
      if (Math.abs(x - startPoint.x) > 5 || Math.abs(y - startPoint.y) > 5) {
        console.log('\n=== ERASING WITH LINE ===');
        console.log('Erase line:', eraseLine);
        
        // Remove all segments that intersect with erase line
        const remainingSegments = lines.filter(segment => {
          const intersects = lineSegmentsIntersect(segment, eraseLine);
          if (intersects) {
            console.log(`Removing segment: ${segment.id}`, segment);
          }
          return !intersects;
        });
        
        console.log(`Segments: ${lines.length} -> ${remainingSegments.length}`);
        addToHistory(remainingSegments);
      }
      
      setIsErasing(false);
      setStartPoint(null);
      setCurrentRect(null);
      return;
    }
    
    if (isDrawing && startPoint && currentRect) {
      // Drawing mode - create rectangle and split into segments
      const newRectLines = createRectangleLines(startPoint.x, startPoint.y, x, y);
      if (newRectLines.length > 0) {
        console.log('\n=== DRAWING RECTANGLE ===');
        console.log(`Rectangle from (${startPoint.x},${startPoint.y}) to (${x},${y})`);
        console.log('New rectangle lines:', newRectLines);
        
        // Combine with existing lines and split all at intersections
        const allLines = [...lines, ...newRectLines];
        const segments = splitAllLinesIntoSegments(allLines);
        
        console.log(`Lines: ${allLines.length} -> Segments: ${segments.length}`);
        console.log('All segments:', segments.map(seg => ({
          id: seg.id,
          from: `(${seg.x1},${seg.y1})`,
          to: `(${seg.x2},${seg.y2})`
        })));
        
        addToHistory(segments);
      }
      
      setIsDrawing(false);
      setStartPoint(null);
      setCurrentRect(null);
    }
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setIsDrawing(false);
    setStartPoint(null);
    setCurrentRect(null);
    setIsErasing(false);
    setIsMeasuring(false);
    setMeasuredSegments([]);
  };

  // Clear all lines
  const clearAll = () => {
    addToHistory([]);
    setPersistentMeasuredSegments(new Set());
    setFilledAreas([]);
  };

  // Export to PNG
  const exportToPNG = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Find the bounding box of all content
    let minX = CANVAS_WIDTH;
    let minY = CANVAS_HEIGHT;
    let maxX = 0;
    let maxY = 0;

    // Check lines
    lines.forEach(line => {
      minX = Math.min(minX, line.x1, line.x2);
      minY = Math.min(minY, line.y1, line.y2);
      maxX = Math.max(maxX, line.x1, line.x2);
      maxY = Math.max(maxY, line.y1, line.y2);
    });

    // Check filled areas
    filledAreas.forEach(area => {
      area.points.forEach(point => {
        minX = Math.min(minX, point.x);
        minY = Math.min(minY, point.y);
        maxX = Math.max(maxX, point.x + GRID_SIZE);
        maxY = Math.max(maxY, point.y + GRID_SIZE);
      });
    });

    // Add margin
    const margin = 40;
    minX = Math.max(0, minX - margin);
    minY = Math.max(0, minY - margin);
    maxX = Math.min(CANVAS_WIDTH, maxX + margin);
    maxY = Math.min(CANVAS_HEIGHT, maxY + margin);

    const exportWidth = maxX - minX;
    const exportHeight = maxY - minY;

    canvas.width = exportWidth;
    canvas.height = exportHeight;

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, exportWidth, exportHeight);

    // Translate to account for cropping
    ctx.save();
    ctx.translate(-minX, -minY);

    // Draw filled areas (no grid dots)
    ctx.fillStyle = 'rgba(204, 204, 204, 0.8)';
    filledAreas.forEach(area => {
      const uniquePoints = new Map<string, {x: number, y: number}>();
      area.points.forEach(p => {
        uniquePoints.set(`${p.x},${p.y}`, p);
      });
      
      uniquePoints.forEach(point => {
        ctx.fillRect(point.x, point.y, GRID_SIZE, GRID_SIZE);
      });
    });

    // Draw lines
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    lines.forEach(line => {
      ctx.beginPath();
      ctx.moveTo(line.x1, line.y1);
      ctx.lineTo(line.x2, line.y2);
      ctx.stroke();
    });

    // Draw measurements if enabled
    if (showMeasurements) {
      ctx.fillStyle = '#000000';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // Get all segments that should show measurements
      const segmentsToMeasure = lines.filter(line => 
        persistentMeasuredSegments.has(line.id)
      );
      
      segmentsToMeasure.forEach(line => {
        const length = getLineLength(line);
        if (length > 0) {
          const midX = (line.x1 + line.x2) / 2;
          const midY = (line.y1 + line.y2) / 2;
          
          // Offset text from line
          const isHorizontal = Math.abs(line.y1 - line.y2) < 5;
          const offsetX = isHorizontal ? 0 : 10;
          const offsetY = isHorizontal ? -10 : 0;
          
          ctx.fillText(length.toString(), midX + offsetX, midY + offsetY);
        }
      });
    }

    ctx.restore();

    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rectangle-drawing-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  // Render filled areas
  const renderFilledAreas = () => {
    return filledAreas.map(area => {
      // Create a polygon from the area points
      const uniquePoints = new Map<string, {x: number, y: number}>();
      area.points.forEach(p => {
        uniquePoints.set(`${p.x},${p.y}`, p);
      });
      
      // Create rectangles for each grid cell
      const rects: React.ReactElement[] = [];
      uniquePoints.forEach((point, key) => {
        rects.push(
          <rect
            key={`${area.id}-${key}`}
            x={point.x}
            y={point.y}
            width={GRID_SIZE}
            height={GRID_SIZE}
            fill="#cccccc"
            opacity="0.8"
          />
        );
      });
      
      return <g key={area.id}>{rects}</g>;
    });
  };

  // Render grid dots
  const renderGridDots = () => {
    const dots = [];
    for (let x = 0; x <= CANVAS_WIDTH; x += GRID_SIZE) {
      for (let y = 0; y <= CANVAS_HEIGHT; y += GRID_SIZE) {
        dots.push(
          <circle
            key={`${x}-${y}`}
            cx={x}
            cy={y}
            r="1"
            fill="#d0d0d0"
          />
        );
      }
    }
    return dots;
  };

  // Render current rectangle or line being drawn
  const renderCurrentRect = () => {
    if (!currentRect || (!isDrawing && !isErasing && !isMeasuring)) return null;
    
    if (isMeasuring) {
      // Render measurement line
      return (
        <line
          key="measure-line"
          x1={currentRect.x1}
          y1={currentRect.y1}
          x2={currentRect.x2}
          y2={currentRect.y2}
          stroke="#0066ff"
          strokeWidth="3"
          strokeDasharray="5,5"
        />
      );
    } else if (isErasing) {
      // Render erase line
      return (
        <line
          key="erase-line"
          x1={currentRect.x1}
          y1={currentRect.y1}
          x2={currentRect.x2}
          y2={currentRect.y2}
          stroke="#ff4444"
          strokeWidth="3"
          strokeDasharray="3,3"
        />
      );
    } else {
      // Render rectangle preview
      const tempLines = createRectangleLines(
        currentRect.x1, 
        currentRect.y1, 
        currentRect.x2, 
        currentRect.y2
      );
      
      return tempLines.map(line => (
        <line
          key={line.id}
          x1={line.x1}
          y1={line.y1}
          x2={line.x2}
          y2={line.y2}
          stroke="#999999"
          strokeWidth="2"
          strokeDasharray="5,5"
        />
      ));
    }
  };

  // Render current dimensions
  const renderCurrentDimensions = () => {
    if (!currentRect || (!isDrawing && !isErasing && !isMeasuring)) return null;
    
    const centerX = (currentRect.x1 + currentRect.x2) / 2;
    const centerY = (currentRect.y1 + currentRect.y2) / 2;
    
    if (isErasing) {
      // Show erase line length
      const dx = Math.abs(currentRect.x2 - currentRect.x1);
      const dy = Math.abs(currentRect.y2 - currentRect.y1);
      const length = Math.max(dx, dy) / GRID_SIZE;
      
      if (length < 0.5) return null; // Don't show for very short lines
      
      return (
        <text
          x={centerX}
          y={centerY - 15}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="16"
          fill="#ff4444"
          fontFamily="Arial"
          fontWeight="bold"
        >
          {Math.round(length)}
        </text>
      );
    } else {
      // Show rectangle dimensions
      const dimensions = getCurrentRectDimensions();
      if (!dimensions || (dimensions.width === 0 && dimensions.height === 0)) return null;
      
      return (
        <text
          x={centerX}
          y={centerY - 10}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="16"
          fill="#333"
          fontFamily="Arial"
          fontWeight="bold"
        >
          {dimensions.width} × {dimensions.height}
        </text>
      );
    }
  };

  // Render measurement labels
  const renderMeasurements = () => {
    if (!showMeasurements) return null;
    
    // Get all segments that should show measurements
    const segmentsToMeasure = lines.filter(line => 
      persistentMeasuredSegments.has(line.id) || measuredSegments.some(seg => seg.id === line.id)
    );
    
    if (segmentsToMeasure.length === 0) return null;
    
    return segmentsToMeasure.map(line => {
      const length = getLineLength(line);
      if (length === 0) return null;
      
      const midX = (line.x1 + line.x2) / 2;
      const midY = (line.y1 + line.y2) / 2;
      
      // Offset text from line
      const isHorizontal = Math.abs(line.y1 - line.y2) < 5;
      const offsetX = isHorizontal ? 0 : 10;
      const offsetY = isHorizontal ? -10 : 0;
      
      // Check if this is currently being measured (for highlighting)
      const isCurrentlyMeasuring = measuredSegments.some(seg => seg.id === line.id);
      
      return (
        <g key={`measure-${line.id}`}>
          {/* Highlight the measured segment only if currently measuring */}
          {isCurrentlyMeasuring && (
            <line
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke="#0066ff"
              strokeWidth="4"
              opacity="0.5"
            />
          )}
          {/* Show the measurement */}
          <text
            x={midX + offsetX}
            y={midY + offsetY}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="16"
            fill="#000000"
            fontFamily="Arial"
            fontWeight="bold"
          >
            {length}
          </text>
        </g>
      );
    });
  };

  return (
    <div className="rectangle-drawer">
      <h2>Rectangle Drawer</h2>
      
      <div className="controls">
        <button 
          onClick={undo} 
          className="control-btn"
          disabled={historyIndex <= 0}
        >
          Undo
        </button>
        <button onClick={clearAll} className="control-btn">
          Clear All
        </button>
        <button 
          onClick={() => setShowMeasurements(!showMeasurements)}
          className={`control-btn ${showMeasurements ? 'active' : ''}`}
        >
          {showMeasurements ? 'Hide' : 'Show'} Measurements
        </button>
        <button 
          onClick={() => setShowFillTool(!showFillTool)}
          className={`control-btn ${showFillTool ? 'active' : ''}`}
        >
          {showFillTool ? 'Hide' : 'Show'} Fill Tool
        </button>
        <button onClick={exportToPNG} className="export-btn">
          Export PNG
        </button>
      </div>

      <div className="instructions">
        <p><strong>Instructions:</strong></p>
        <p>• Click and drag to draw rectangles (dimensions shown while drawing)</p>
        <p>• Hold Ctrl and drag to draw an erase line that removes overlapping segments</p>
        <p>• When "Show Measurements" is on: draw blue lines to measure segments they intersect</p>
        <p>• When "Show Fill Tool" is on: click inside enclosed areas to fill with grey</p>
        <p>• Use Undo to revert last action</p>
      </div>
      
      <div className="drawing-area">
        <svg
          ref={svgRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="drawing-surface"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
        >
          {/* Grid dots */}
          {renderGridDots()}
          
          {/* Filled areas (render before lines so lines appear on top) */}
          {renderFilledAreas()}
          
          {/* Existing lines */}
          {lines.map(line => (
            <line
              key={line.id}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
              stroke="#000000"
              strokeWidth="2"
            />
          ))}
          
          {/* Current rectangle being drawn */}
          {renderCurrentRect()}
          
          {/* Current dimensions */}
          {renderCurrentDimensions()}
          
          {/* Measurements */}
          {renderMeasurements()}
        </svg>
      </div>

      {/* Hidden canvas for export */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default RectangleDrawer;